import moment from 'moment';
import QuestionnaireMutator from '../../unsw-fhir-questionnaire/Utilities/QuestionnaireMutator';
import { ICodeableConcept } from '../../unsw-fhir/CodeableConcept';
import {
  ConditionClinicalStatus,
  ConditionVerificationStatus,
  ICondition,
} from '../../unsw-fhir/Condition';
import { IPatient } from '../../unsw-fhir/Patient';
import { IPractitioner } from '../../unsw-fhir/Practitioner';
import { IQuestionnaire } from '../../unsw-fhir/Questionnaire';
import {
  IQuestionnaireResponse,
  QuestionnaireResponseItemComponent,
} from '../../unsw-fhir/QuestionnaireResponse';
import {
  isCodingAnswerComponent,
  isQuestionnaireResponseItemAnswer,
} from '../../unsw-fhir/QuestionnaireResponse/typeGuards';
import {
  createPatientReference,
  createPractitionerReference,
  getReferenceId,
} from '../../unsw-fhir/Reference';

const createCondition = ({
  patient,
  practitioner,
  conditionCode,
  clinicalStatus = 'active',
  verificationStatus = 'provisional',
}: {
  patient: IPatient;
  practitioner: IPractitioner;
  conditionCode: ICodeableConcept;
  clinicalStatus?: ConditionClinicalStatus;
  verificationStatus?: ConditionVerificationStatus;
}): ICondition => {
  const condition: ICondition = {
    resourceType: 'Condition',
    clinicalStatus,
    verificationStatus,
    subject: createPatientReference(patient.id!),
    asserter: createPractitionerReference(practitioner.id!),
    assertedDate: moment().format('YYYY-MM-DD'),
    code: conditionCode,
  };

  return condition;
};

export const SNOMED_CDK_CODING = {
  system: 'http://snomed.info/sct',
  code: '709044004',
  display: 'Chronic kidney disease (disorder)',
};

const createCDKCondition = (
  patient: IPatient,
  practitioner: IPractitioner,
): ICondition => {
  return createCondition({
    patient,
    practitioner,
    conditionCode: {
      coding: [SNOMED_CDK_CODING],
    },
  });
};

export const SNOMED_DEMENTIA_CODING = {
  system: 'http://snomed.info/sct',
  code: '52448006',
  display: 'Dementia (disorder)',
};

const createDementiaCondition = (
  patient: IPatient,
  practitioner: IPractitioner,
): ICondition => {
  return createCondition({
    patient,
    practitioner,
    conditionCode: {
      coding: [SNOMED_DEMENTIA_CODING],
    },
  });
};

const getConditionFromResponseItem = (
  item: QuestionnaireResponseItemComponent,
  patient: IPatient,
  practitioner: IPractitioner,
): ICondition[] => {
  if (!isQuestionnaireResponseItemAnswer(item)) return [];
  return item.answer
    .map(answer => {
      if (isCodingAnswerComponent(answer) && answer.valueCoding.code) {
        switch (answer.valueCoding.code) {
          case 'ckd':
            return createCDKCondition(patient, practitioner);
          default:
            return undefined;
        }
      }
      return undefined;
    })
    .filter((condition): condition is ICondition => !!condition);
};

const isDementiaConditionFromResponseItem = (
  item: QuestionnaireResponseItemComponent,
  patient: IPatient,
  practitioner: IPractitioner,
) => {
  if (!isQuestionnaireResponseItemAnswer(item)) return [];
  return item.answer
    .map(answer => {
      if (isCodingAnswerComponent(answer) && answer.valueCoding.code) {
        switch (answer.valueCoding.code) {
          case 'yes':
            return createDementiaCondition(patient, practitioner);
          default:
            return undefined;
        }
      }
      return undefined;
    })
    .filter((condition): condition is ICondition => !!condition);
};

export default class CristalConditionsGenerator {
  /* 
  This a magic variable that is the unqine linkId for the qusetion 
  that answers the conditions
   */
  static QUESTIONNAIRE_ITEM_KEY: string = '2-1';
  static QUESTIONNAIRE_DEMENTIA_ITEM_KEY = '3-1';

  private questionnaire: IQuestionnaire;
  private mutator: QuestionnaireMutator;

  constructor(questionnaire: IQuestionnaire) {
    this.questionnaire = questionnaire;
    if (!this.questionnaire.id)
      throw new Error('Questionnaire must have an Id');

    this.mutator = new QuestionnaireMutator(this.questionnaire);
  }

  private scanConditions = (
    key: string,
    patient: IPatient,
    practitioner: IPractitioner,
  ) => {
    const container = this.mutator.getQuestionnaireResponse(key);
    if (!container) return [];

    const response = container.value;

    return getConditionFromResponseItem(response, patient, practitioner);
  };

  private scanDementia = (
    key: string,
    patient: IPatient,
    practitioner: IPractitioner,
  ) => {
    const container = this.mutator.getQuestionnaireResponse(key);
    if (!container) return [];

    const response = container.value;

    return isDementiaConditionFromResponseItem(response, patient, practitioner);
  };

  generateConditions = (
    questionnaireResponse: IQuestionnaireResponse,
    patient: IPatient,
    practitioner: IPractitioner,
  ): ICondition[] => {
    if (
      getReferenceId(questionnaireResponse.questionnaire) !==
      this.questionnaire.id
    )
      throw new Error('Questionnaire must be a CristalQuestionnaire');

    this.mutator.updateResponse(questionnaireResponse);

    return [
      ...this.scanConditions(
        CristalConditionsGenerator.QUESTIONNAIRE_ITEM_KEY,
        patient,
        practitioner,
      ),
      ...this.scanDementia(
        CristalConditionsGenerator.QUESTIONNAIRE_DEMENTIA_ITEM_KEY,
        patient,
        practitioner,
      ),
    ];
  };
}
